export const UNAUTHORIZED_CLOSE_CODE = 4401;

export interface SubscriptionCloseFields {
	code: number | undefined;
	name: string | undefined;
	reason: string;
	wasClean: boolean | undefined;
}

interface GraphqlErrorEntry {
	extensions?: { code?: unknown };
	message?: unknown;
}

const isCloseEventLike = (error: unknown): error is { code?: unknown; reason?: unknown; wasClean?: unknown } =>
	!!error && typeof error === "object" && "code" in error && "wasClean" in error;

const toGraphqlErrorEntries = (error: unknown): GraphqlErrorEntry[] =>
	Array.isArray(error) ? (error as GraphqlErrorEntry[]) : [];

const toGraphqlMessages = (entries: GraphqlErrorEntry[]): string[] =>
	entries.map((entry) => (typeof entry?.message === "string" ? entry.message : "")).filter(Boolean);

const firstGraphqlCode = (entries: GraphqlErrorEntry[]): string | undefined =>
	entries.map((entry) => entry?.extensions?.code).find((code): code is string => typeof code === "string");

const GRAPHQL_ERROR_NAME = "GraphQLError";

// A CloseEvent has no enumerable own properties, so serializing it yields {"isTrusted": true} and drops the rest.
const describeSubscriptionClose = (error: unknown): SubscriptionCloseFields => {
	if (isCloseEventLike(error)) {
		return {
			code: typeof error.code === "number" ? error.code : undefined,
			name: undefined,
			reason: typeof error.reason === "string" ? error.reason : "",
			wasClean: error.wasClean === true
		};
	}

	const graphqlErrors = toGraphqlErrorEntries(error);
	const graphqlMessages = toGraphqlMessages(graphqlErrors);
	if (graphqlMessages.length) {
		return {
			code: undefined,
			name: firstGraphqlCode(graphqlErrors) ?? GRAPHQL_ERROR_NAME,
			reason: graphqlMessages.join("; "),
			wasClean: undefined
		};
	}

	if (error instanceof Error) {
		return { code: undefined, name: error.name, reason: error.message, wasClean: undefined };
	}

	return { code: undefined, name: undefined, reason: "", wasClean: undefined };
};

export const shouldRecreateSubscriptionClient = (
	closeCode: number | undefined,
	recreatedSinceConnect: boolean
): boolean => closeCode === UNAUTHORIZED_CLOSE_CODE && !recreatedSinceConnect;

export default describeSubscriptionClose;
